"use client"

import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { Drawer as DrawerPrimitive } from "vaul"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const ResponsiveDialogContext = React.createContext(false)

function useIsMobileDialog() {
  const [isMobile, setIsMobile] = React.useState(false)

  React.useEffect(() => {
    const media = window.matchMedia("(max-width: 767px)")
    const update = () => setIsMobile(media.matches)
    update()
    media.addEventListener("change", update)
    return () => media.removeEventListener("change", update)
  }, [])

  return isMobile
}

const Dialog = ({ children, ...props }) => {
  const isMobile = useIsMobileDialog()
  const Root = isMobile ? DrawerPrimitive.Root : DialogPrimitive.Root

  return (
    <ResponsiveDialogContext.Provider value={isMobile}>
      <Root {...props}>{children}</Root>
    </ResponsiveDialogContext.Provider>
  )
}

const DialogTrigger = React.forwardRef(({ children, ...props }, ref) => {
  const isMobile = React.useContext(ResponsiveDialogContext)
  const Trigger = isMobile ? DrawerPrimitive.Trigger : DialogPrimitive.Trigger
  return <Trigger ref={ref} {...props}>{children}</Trigger>
})
DialogTrigger.displayName = "DialogTrigger"

const DialogPortal = ({ children, ...props }) => {
  const isMobile = React.useContext(ResponsiveDialogContext)
  const Portal = isMobile ? DrawerPrimitive.Portal : DialogPrimitive.Portal
  return <Portal {...props}>{children}</Portal>
}

const DialogClose = React.forwardRef(({ children, ...props }, ref) => {
  const isMobile = React.useContext(ResponsiveDialogContext)
  const Close = isMobile ? DrawerPrimitive.Close : DialogPrimitive.Close
  return <Close ref={ref} {...props}>{children}</Close>
})
DialogClose.displayName = "DialogClose"

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => {
  const isMobile = React.useContext(ResponsiveDialogContext)
  const Overlay = isMobile ? DrawerPrimitive.Overlay : DialogPrimitive.Overlay

  return (
    <Overlay
      ref={ref}
      className={cn(
        "ds-overlay fixed inset-0 z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className
      )}
      {...props}
    />
  )
})
DialogOverlay.displayName = "DialogOverlay"

const DialogContent = React.forwardRef(({ className, children, ...props }, ref) => {
  const isMobile = React.useContext(ResponsiveDialogContext)
  const Content = isMobile ? DrawerPrimitive.Content : DialogPrimitive.Content

  return (
    <DialogPortal>
      <DialogOverlay />
      <Content
        ref={ref}
        className={cn(
          isMobile
            ? "ds-card fixed inset-x-0 bottom-0 z-50 flex max-h-[92dvh] w-full flex-col overflow-y-auto rounded-b-none rounded-t-[var(--radius-panel)] p-5"
            : "ds-card fixed left-[50%] top-[50%] z-50 grid max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 overflow-y-auto p-6 duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
          className
        )}
        {...props}
      >
        {isMobile && <div className="mx-auto mb-3 h-1.5 w-12 rounded-full bg-muted-foreground/30" />}
        {children}
        <DialogClose className="ds-close-button ds-focus-ring absolute right-4 top-4 opacity-80 hover:opacity-100 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogClose>
      </Content>
    </DialogPortal>
  )
})
DialogContent.displayName = "DialogContent"

const DialogHeader = ({ className, ...props }) => (
  <div className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)} {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({ className, ...props }) => (
  <div className={cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:space-x-2", className)} {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => {
  const isMobile = React.useContext(ResponsiveDialogContext)
  const Title = isMobile ? DrawerPrimitive.Title : DialogPrimitive.Title
  return <Title ref={ref} className={cn("text-lg font-semibold leading-none tracking-tight", className)} {...props} />
})
DialogTitle.displayName = "DialogTitle"

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => {
  const isMobile = React.useContext(ResponsiveDialogContext)
  const Description = isMobile ? DrawerPrimitive.Description : DialogPrimitive.Description
  return <Description ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
})
DialogDescription.displayName = "DialogDescription"

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}